// TimelineGrid.js - Main timeline area with tasks and click handling
import React, { forwardRef, useState, useEffect, useMemo } from 'react';
import { Box, alpha, useTheme } from '@mui/material';
import TimelineHeader from './TimelineHeader';
import CategoryRow from '../category/CategoryRow';
import DayGrid from './DayGrid';
import NonWorkingDayOverlay from './NonWorkingDayOverlay';
import CategorySidebar from '../sidebar/CategorySidebar';
import { useTimelineLogic } from '../../hooks/useTimelineLogic';
import { useAutoScroll } from '../../hooks/useAutoScroll';
import { useGanttDnd } from '../../context/GanttDndContext';
import { getCategoryHeight } from '../../utils/ganttUtils';

const TimelineGrid = forwardRef(({
  onScroll,
  scrollState,
  categories,
  baseDate,
  dayWidth,
  onTaskClick,
  rowNameWidth,
  nonWorkingDaysMap = null,
  isNonWorkingDay = null,
  getNonWorkingDayNames = null,
  getNonWorkingDayEntries = null,
  renderRows = null,
  renderLabel = null,
  renderHeader = null,
  headerHeight = 108,
  getCategoryRowHeight = null,
  sidebarHeaderHeight = null,
  sidebarTitle,
  visibleDaysOverride = null,
  showBaseDateIndicator = true,
  unavailabilityBlocks = [],
}, ref) => {
  const theme = useTheme();
  const [containerWidth, setContainerWidth] = useState(0);
  const { draggingTask } = useGanttDnd();

  // Enable auto-scrolling during drag operations
  useAutoScroll(!!draggingTask, ref);

  // Track container width changes with ResizeObserver
  useEffect(() => {
    if (!ref?.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        // Get the width minus the sidebar width to get actual timeline width
        const width = entry.contentRect.width - rowNameWidth;
        setContainerWidth(width);
      }
    });

    resizeObserver.observe(ref.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [ref, rowNameWidth]);

  // Calculate visible days based on container width (override renders all slots at once)
  const visibleDays = visibleDaysOverride ?? (containerWidth > 0 ? Math.ceil(containerWidth / dayWidth) : 40);

  const {
    visibleDayIndices,
    monthGroups,
    weekGroups,
    contentOffset,
    totalWidth,
    getDateForDayIndex,
    formatDay
  } = useTimelineLogic(scrollState, baseDate, dayWidth, visibleDays);

  const totalHeight = useMemo(
    () => {
      const getHeight = getCategoryRowHeight ?? ((cat) => getCategoryHeight(cat.tasks));
      return categories.reduce((sum, cat) => sum + getHeight(cat), 0) + headerHeight;
    },
    [categories, headerHeight, getCategoryRowHeight]
  );

  return (
    <Box
      ref={ref}
      onScroll={onScroll}
      sx={{
        flex: 1,
        overflowX: 'auto',
        overflowY: 'auto',
        position: 'relative',
        '&::-webkit-scrollbar': {
          height: 8,
          width: 8,
        },
        '&::-webkit-scrollbar-track': {
          bgcolor: alpha(theme.palette.grey[200], 0.5),
        },
        '&::-webkit-scrollbar-thumb': {
          bgcolor: theme.palette.grey[400],
          borderRadius: 1,
          '&:hover': {
            bgcolor: theme.palette.grey[500],
          },
        },
      }}
    >
      {/* Sticky Category Labels Column - Floated to stay on left during horizontal scroll */}
      <CategorySidebar
        categories={categories}
        rowNameWidth={rowNameWidth}
        totalHeight={totalHeight}
        renderLabel={renderLabel}
        getCategoryRowHeight={getCategoryRowHeight}
        sidebarHeaderHeight={sidebarHeaderHeight ?? headerHeight}
        sidebarTitle={sidebarTitle}
      />

      {/* Virtual container with dynamic width */}
      <Box sx={{
        width: `${totalWidth}px`,
        height: `${totalHeight}px`,
        position: 'relative',
        marginLeft: `${rowNameWidth}px`,
        zIndex: 1
      }}>
        {/* Visible content container */}
        <Box sx={{
          position: 'absolute',
          left: `${contentOffset}px`,
          width: `${visibleDayIndices.length * dayWidth}px`,
          height: '100%',
          zIndex: 1
        }}>
          {renderHeader
            ? renderHeader({ visibleDayIndices, getDateForDayIndex, dayWidth, monthGroups, weekGroups, formatDay })
            : (
              <TimelineHeader
                monthGroups={monthGroups}
                weekGroups={weekGroups}
                visibleDayIndices={visibleDayIndices}
                getDateForDayIndex={getDateForDayIndex}
                formatDay={formatDay}
                dayWidth={dayWidth}
                stickyLeft={rowNameWidth}
              />
            )
          }

          {/* Category Rows */}
          <Box sx={{ bgcolor: 'background.paper', position: 'relative' }}>
            {/* Shared day grid for all rows - renders once instead of per row */}
            <DayGrid
              visibleDayIndices={visibleDayIndices}
              getDateForDayIndex={getDateForDayIndex}
              dayWidth={dayWidth}
              totalHeight={totalHeight - headerHeight}
              showBaseDateIndicator={showBaseDateIndicator}
            />

            {renderRows
              ? renderRows({ categories, visibleDayIndices, getDateForDayIndex, dayWidth, baseDate, onTaskClick, contentOffset })
              : categories.map((category, categoryIndex) => (
                  <CategoryRow
                    key={category.id}
                    category={category}
                    categoryIndex={categoryIndex}
                    visibleDayIndices={visibleDayIndices}
                    getDateForDayIndex={getDateForDayIndex}
                    dayWidth={dayWidth}
                    baseDate={baseDate}
                    onTaskClick={onTaskClick}
                    unavailabilityBlocks={unavailabilityBlocks.filter((b) => b.staff_member_id === category.id)}
                  />
                ))
            }

            {/* Non-Working Day Overlays (public holidays + closing days) */}
            {isNonWorkingDay && visibleDayIndices.map((dayIndex) => {
              const date = getDateForDayIndex(dayIndex);
              if (!isNonWorkingDay(date)) return null;

              const names = getNonWorkingDayNames(date);
              const displayText = names?.join(', ') || '';
              const position = visibleDayIndices.indexOf(dayIndex);
              const entries = getNonWorkingDayEntries ? getNonWorkingDayEntries(date) : null;
              // Full-day entry wins over partial for the overlay's visual
              // weight, mirroring the backend's own tie-break rule — same
              // reasoning as day_processor_service's closure resolution.
              const primaryKind = entries?.find((e) => e.is_full_day)?.kind
                ?? entries?.[0]?.kind
                ?? null;

              return (
                <NonWorkingDayOverlay
                  key={`non-working-${dayIndex}`}
                  position={position}
                  dayWidth={dayWidth}
                  name={displayText}
                  kind={primaryKind}
                />
              );
            })}
          </Box>
        </Box>
      </Box>
    </Box>
  );
});

export default TimelineGrid;